import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useLocation } from "react-router";
import { useResetPassword } from "@/lib/hooks";
import { ErrorMessage } from "@/components/ErrorMessage";

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Passwords do not match",
        path: ["confirmPassword"],
      });
    }
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage(): React.ReactElement {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  const resetMutation = useResetPassword();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-[400px]">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h1 className="mb-4 text-center text-2xl font-bold text-gray-900">
              Invalid Reset Link
            </h1>
            <p className="mb-4 text-center text-sm text-gray-600">
              Invalid or missing reset link. Please request a new one.
            </p>
            <p className="text-center text-sm">
              <Link
                to="/forgot-password"
                className="text-blue-600 hover:text-blue-800"
              >
                Request new reset link
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: ResetPasswordFormData): Promise<void> => {
    await resetMutation.mutateAsync({ token, password: data.password });
  };

  if (resetMutation.isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-[400px]">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h1 className="mb-4 text-center text-2xl font-bold text-gray-900">
              Password Reset
            </h1>
            <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              Password reset successfully. You can now log in with your new
              password.
            </div>
            <p className="text-center text-sm">
              <Link
                to="/login"
                className="text-blue-600 hover:text-blue-800"
              >
                Go to login
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-[400px]">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="mb-6 text-center text-2xl font-bold text-gray-900">
            Reset Password
          </h1>

          <ErrorMessage
            message={
              resetMutation.error instanceof Error
                ? resetMutation.error.message
                : undefined
            }
          />

          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            className="mt-4 space-y-4"
          >
            <div>
              <label
                htmlFor="password"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                New Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                aria-describedby="password-hint"
                {...register("password")}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p id="password-hint" className="mt-1 text-xs text-gray-500">
                Must be at least 8 characters.
              </p>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register("confirmPassword")}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || resetMutation.isPending}
              className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {resetMutation.isPending
                ? "Resetting..."
                : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
